import { useCallback, useEffect, useState } from "react";

import { ApiFailure, api, subscribe, type BotStatus, type Me, type PublicUser } from "./lib/api";
import { Badge, Skeleton } from "./components/ui";
import { linkProps, useRouter, type Route } from "./lib/router";
import { SignIn } from "./pages/SignIn";
import { Dashboard } from "./pages/Dashboard";
import { Proposals } from "./pages/Proposals";
import { Conversations } from "./pages/Conversations";
import { Keys } from "./pages/Keys";
import { Webhooks } from "./pages/Webhooks";
import { Settings } from "./pages/Settings";
import { Users } from "./pages/Users";

const NAV: { route: Route; label: string; scope?: string }[] = [
  { route: "dashboard", label: "Dashboard" },
  { route: "proposals", label: "Proposals" },
  { route: "chats", label: "Chats" },
  { route: "keys", label: "API keys" },
  { route: "webhooks", label: "Webhooks" },
  { route: "users", label: "Users" },
  { route: "settings", label: "Settings" },
];

/** Roles that may see each section. The server enforces it; this only hides what would 403. */
const VISIBLE_TO: Record<Route, PublicUser["role"][]> = {
  dashboard: ["owner", "admin", "operator", "viewer"],
  proposals: ["owner", "admin", "operator", "viewer"],
  chats: ["owner", "admin", "operator", "viewer"],
  keys: ["owner", "admin"],
  webhooks: ["owner", "admin", "operator", "viewer"],
  users: ["owner", "admin"],
  settings: ["owner", "admin"],
};

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const { route, navigate } = useRouter();
  const [streamLive, setStreamLive] = useState(false);
  const [bot, setBot] = useState<BotStatus | null>(null);

  const load = useCallback(async () => {
    try {
      setMe(await api.get<Me>("/auth/me"));
    } catch (error) {
      // 401 is the normal "not signed in yet" path, not a failure worth showing.
      if (!(error instanceof ApiFailure && error.isUnauthenticated)) console.error(error);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // One stream for the whole shell. Individual pages subscribe to the same
  // EventSource through their own effects rather than opening a second one.
  useEffect(() => {
    if (!me?.user) return;
    return subscribe([], () => undefined, setStreamLive);
  }, [me?.user]);

  useEffect(() => {
    if (!me?.user) return;
    const tick = () => void api.get<BotStatus>("/stats/bot").then(setBot).catch(() => undefined);
    tick();
    // The gateway's own state is not an event, so it is polled — sparingly.
    const timer = setInterval(tick, 20_000);
    return () => clearInterval(timer);
  }, [me?.user]);

  if (loading) {
    return (
      <div className="mx-auto grid max-w-[1200px] gap-4 p-8">
        <Skeleton className="h-9 w-52" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    );
  }

  if (!me?.user) return <SignIn onSignedIn={() => void load()} />;

  const user = me.user;
  const visible = NAV.filter((n) => VISIBLE_TO[n.route].includes(user.role));

  return (
    <div className="flex min-h-full">
      <aside className="flex w-[228px] shrink-0 flex-col border-r border-[var(--border)] bg-[var(--surface-1)]">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <div className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--brand)] text-[13px] font-bold text-[var(--brand-ink)]">
            SQ
          </div>
          <div className="leading-tight">
            <div className="text-[14px] font-semibold">SentryQ Bot</div>
            <div className="text-[11px] text-[var(--faint)]">Approval console</div>
          </div>
        </div>

        <nav className="flex flex-col gap-0.5 px-3">
          {visible.map((item) => (
            <a
              key={item.route}
              {...linkProps(item.route, navigate)}
              className={
                "rounded-[10px] px-3 py-2 text-[13px] transition-colors duration-150 " +
                (route === item.route
                  ? "bg-[var(--brand-soft)] font-medium text-[var(--brand)]"
                  : "text-[var(--muted)] hover:bg-[var(--surface-2)] hover:text-[var(--foreground)]")
              }
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="mt-auto border-t border-[var(--border)] p-4">
          <BotPill bot={bot} streamLive={streamLive} />
          <div className="mt-3 truncate text-[12px] text-[var(--muted)]" title={user.email}>
            {user.email}
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <Badge tone={user.role === "owner" ? "brand" : "neutral"}>{user.role}</Badge>
            <button
              onClick={() => {
                void api.post("/auth/sign-out").then(() => setMe(null));
              }}
              className="text-[12px] text-[var(--faint)] transition-colors hover:text-[var(--foreground)]"
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1200px] p-8">
          {route === "dashboard" && <Dashboard />}
          {route === "proposals" && <Proposals user={user} />}
          {route === "chats" && <Conversations user={user} />}
          {route === "keys" && <Keys />}
          {route === "webhooks" && <Webhooks />}
          {route === "users" && <Users me={user} />}
          {route === "settings" && <Settings />}
        </div>
      </main>
    </div>
  );
}

/**
 * The connection pill.
 *
 * Two independent things, shown as one: whether the Discord gateway is up, and
 * whether this browser's event stream is live. They fail separately and an
 * operator needs to know which — "the bot is down" and "my page has gone stale"
 * call for very different reactions.
 */
function BotPill({ bot, streamLive }: { bot: BotStatus | null; streamLive: boolean }) {
  const connected = bot?.connected ?? false;
  return (
    <div className="flex flex-col gap-1.5 text-[12px]">
      <div className="flex items-center gap-2">
        <span
          className={
            "h-1.5 w-1.5 shrink-0 rounded-full " +
            (connected ? "bg-[var(--ok)] animate-pulse-dot" : "bg-[var(--danger)]")
          }
        />
        <span className="text-[var(--muted)]">
          {connected ? `Discord · ${bot?.pingMs ?? "?"}ms` : "Discord offline"}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={
            "h-1.5 w-1.5 shrink-0 rounded-full " + (streamLive ? "bg-[var(--ok)]" : "bg-[var(--faint)]")
          }
        />
        <span className="text-[var(--faint)]">{streamLive ? "Live updates on" : "Reconnecting…"}</span>
      </div>
    </div>
  );
}
